<div class="flex h-full w-full flex-col overflow-hidden">
  @if (!selectedTransformer()) {
    <app-upload-zone
      class="h-full"
      [config]="uploadConfig()"
      (uploadClicked)="onFilesDropUpload($event)" />
  } @else {
    <div class="border-weak bg-elevated flex items-center gap-3 border-b px-4 py-2.5">
      <span
        class="bg-accent-interactive text-accent-main flex h-7 w-7 shrink-0 items-center justify-center rounded-md">
        <i [class]="ICONS.database" class="text-sm"></i>
      </span>
      <div class="flex min-w-0 flex-col leading-tight">
        <span class="text-text-primary text-sm font-semibold">
          {{ 'features.data.upload.header.title' | translate }}
        </span>
        <span class="text-text-tertiary text-2xs truncate font-mono">
          {{
            'features.data.upload.header.subtitle'
              | translate
                : { transformer: selectedTransformerName(), entity: entityDisplayName() }
          }}
        </span>
      </div>
      @if (targetPlanName(); as planName) {
        <span
          class="border-accent-border bg-accent-interactive text-accent-main inline-flex min-w-0 items-center gap-1.5 rounded-full border px-2.5 py-0.5 text-xs font-medium">
          <i [class]="ICONS.target" class="text-sm"></i>
          <span class="truncate">
            {{ 'features.data.upload.header.plan_target' | translate: { plan: planName } }}
          </span>
        </span>
      }
      <div class="flex-1"></div>
      <app-button
        type="primary"
        size="sm"
        [icon]="ICONS.upload"
        [disabled]="isUploading() || isParsingCsv() || rows().length === 0"
        [label]="'features.data.upload.header.import' | translate: { count: rows().length }"
        (clickEvent)="handleCsvUploadWithTransformer()" />
    </div>

    <div class="border-weak bg-elevated flex items-center gap-3 border-b px-4 py-2">
      <app-button-select
        [items]="createTransformerSelectItems()"
        [value]="selectedTransformerId()"
        [triggerIconClass]="ICONS.database"
        [enableSearch]="false"
        (valueChange)="onTransformerSelect($event)" />
      <span class="text-text-tertiary text-xs">
        {{
          'features.data.upload.header.rows_entered' | translate: { count: rows().length }
        }}
      </span>
      <div class="flex-1"></div>
      @if (rows().length > 0) {
        <span
          class="bg-success-soft text-success inline-flex items-center gap-1.5 rounded-full px-2.5 py-0.5 text-xs font-medium">
          <i [class]="ICONS.checkCircle" class="text-sm"></i>
          {{
            'features.data.upload.header.valid_count'
              | translate: { count: validRowCount() }
          }}
        </span>
        @if (errors().length > 0) {
          <span
            class="bg-danger-soft text-danger inline-flex items-center gap-1.5 rounded-full px-2.5 py-0.5 text-xs font-medium">
            <i [class]="ICONS.warningCircle" class="text-sm"></i>
            {{
              'features.data.upload.header.error_count'
                | translate: { count: errors().length }
            }}
          </span>
        }
      }
      <app-button
        type="icon"
        size="sm"
        [icon]="ICONS.eraser"
        [tooltip]="'features.data.upload.toolbox.reset' | translate"
        (clickEvent)="resetUpload()" />
    </div>

    <div class="border-weak bg-surface flex items-center gap-3 border-b px-4 py-2">
      <input
        #csvFileInput
        type="file"
        class="hidden"
        accept=".csv,text/csv,text/plain"
        (change)="onBrowseCsvSelected($event)" />
      <span class="text-text-secondary inline-flex items-center gap-2 text-xs">
        <i [class]="ICONS.upload" class="text-accent-main text-sm"></i>
        <span class="text-text-secondary text-xs">{{
          'features.data.upload.drop_csv_hint' | translate
        }}</span>
      </span>
      <span class="text-text-tertiary text-2xs font-mono">
        {{
          'features.data.upload.mapping_hint'
            | translate: { transformer: selectedTransformerName() }
        }}
      </span>
      <div class="flex-1"></div>
      <app-button
        type="cancel"
        size="sm"
        [icon]="ICONS.folderDashed"
        [label]="'features.data.upload.browse' | translate"
        (clickEvent)="csvFileInput.click()" />
    </div>

    <div
      class="relative flex h-full min-h-0 flex-col justify-between"
      (dragover)="onCsvDragOver($event)"
      (dragleave)="onCsvDragLeave($event)"
      (drop)="onCsvDrop($event)">
      <app-table
        [data]="rows()"
        [tableId]="makeTableId()"
        [allColumns]="columns()"
        [actions]="actions()"
        [errors]="errors()"
        (indexMap)="reIndexErrors($event)"
        (rowEdit)="handleRowEdition($event)"
        [config]="tableConfig"
        class="rounded-sm" />

      <div class="w-full grow"></div>

      @if (errors().length > 0) {
        <app-errors-list
          class="bg-surface border-danger flex max-h-52 shrink-0 flex-col border-t-2 shadow"
          [errors]="errors()" />
      }

      @if (isParsingCsv()) {
        <div
          class="bg-surface/80 absolute inset-0 z-30 flex flex-col items-center justify-center gap-3"
          role="status"
          aria-live="polite">
          <app-spinner />
          <span class="text-text-secondary text-sm font-medium">
            {{ 'features.data.upload.parsing' | translate }}
          </span>
        </div>
      }

      @if (isDraggingCsv()) {
        <div
          class="border-accent-main bg-accent-interactive/90 pointer-events-none absolute inset-2 z-30 flex flex-col items-center justify-center gap-2 rounded-lg border-2 border-dashed">
          <i [class]="ICONS.upload" class="text-accent-main text-3xl"></i>
          <span class="text-accent-main text-sm font-semibold">
            {{ 'features.data.upload.drop_overlay' | translate }}
          </span>
          <span class="text-accent-main text-2xs font-mono opacity-80">
            {{
              'features.data.upload.mapping_hint'
                | translate: { transformer: selectedTransformerName() }
            }}
          </span>
        </div>
      }
    </div>
  }
</div>
